import { v4 as uuidv4 } from "uuid";
import type { SupabaseClient } from "@supabase/supabase-js";

type PublishWorkshopInput = {
  id?: string;
  workshop_key?: string;
  title?: string;
  description?: string | null;
  skill_level?: string | null;
  start_at: string;
  end_at: string;
  duration_minutes?: number | null;
  capacity?: number;
  seats_taken?: number;
  price_cents?: number;
  currency?: string;
  hero_image_url?: string | null;
  media_gallery?: unknown | null;
};

export async function publishWorkshop({
  supabase,
  workshop,
  studioId,
}: {
  supabase: SupabaseClient;
  workshop: PublishWorkshopInput;
  studioId: string;
}): Promise<any> {
  // HARD VALIDATION — fail fast
  if (!workshop.start_at || !workshop.end_at) {
    throw new Error("Start and end time required before publishing.");
  }

  const id = workshop.id || uuidv4();
  const workshop_key = workshop.workshop_key || id;

  const payload = {
    id,
    workshop_key,
    studio_id: studioId,

    title: workshop.title ?? "",
    description: workshop.description ?? null,
    skill_level: workshop.skill_level ?? null,

    start_at: workshop.start_at,
    end_at: workshop.end_at,
    duration_minutes: workshop.duration_minutes ?? null,

    capacity: workshop.capacity ?? 0,
    seats_taken: workshop.seats_taken ?? 0,

    price_cents: workshop.price_cents ?? 0,
    currency: workshop.currency ?? "AUD",

    hero_image_url: workshop.hero_image_url ?? null,
    media_gallery: workshop.media_gallery ?? null,

    is_published: true,
    deleted_at: null,
    cancelled_at: null,
    archived_at: null,
    updated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from("workshops")
    .upsert(payload, { onConflict: "id" })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function unpublishWorkshop({
  supabase,
  workshopIdOrKey,
}: {
  supabase: SupabaseClient;
  workshopIdOrKey: string;
}): Promise<any> {
  // 1) Find workshop_key (works even if caller passes id)
  const { data: row, error: findErr } = await supabase
    .from("workshops")
    .select("id, workshop_key")
    .eq("id", workshopIdOrKey)
    .maybeSingle();

  const workshopKey = row?.workshop_key || workshopIdOrKey; // fallback: assume caller passed key

  // 2) Unpublish ALL rows with this workshop_key
  const { data, error } = await supabase
    .from("workshops")
    .update({
      is_published: false,
      updated_at: new Date().toISOString(),
    })
    .eq("workshop_key", workshopKey)
    .select("id,workshop_key,is_published");

  if (error) throw error;
  return data;
}
